/**
 * Processing fee calculation for the client portal.
 *
 * Duplicated per slice by design — each slice deploys independently, so a cross-slice import
 * would not exist at runtime. Sibling copies that MUST stay in agreement:
 *   API/modules/common/processingFeeUtils.js       (the server, authoritative)
 *   Web/admin-portal/src/utils/processingFee.js
 *   iOS / Android equivalents
 *
 * SCOPE NOTE: the client portal is customer-facing and is DISPLAY ONLY. Customers do not get
 * an opt-out toggle and cannot edit the rate — those controls live in the admin portal. So
 * there is no `enabled` argument here: whether the fee applies is decided by the document's
 * own config, which the shop set.
 */

function roundCurrency(value) {
	return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100
}

export function calculateProcessingFee({ amount, processingFee }) {
	if (!processingFee || !processingFee.active) return 0

	// The shop's decision, not the customer's. The portal only ever takes card payments, so the
	// credit-card-only autoApply setting is the right gate.
	//
	// Deliberately ignores `processingFee.status`: buildProcessingFee() on the API initialises
	// it to `company.processingFee.active` for every document, so it carries no user intent and
	// treating it as "on" would charge a fee the shop never opted into.
	if (!processingFee.autoApply) return 0

	const feeDetails = processingFee.feeDetails || {}
	const rateAmount = Number(feeDetails.rateAmount || 0)
	if (rateAmount <= 0) return 0

	// Number() is load-bearing: the admin FormSelect persists rateType as the STRING '0'/'1',
	// so a strict === 1 would treat a flat fee as a percentage.
	if (Number(feeDetails.rateType) === 1) return roundCurrency(rateAmount)

	const rate = rateAmount / 100
	const base = Number(amount || 0)

	if (processingFee.calculationMode === 'afterFee') {
		// Guard the degenerate rate >= 100% case (divide by zero / negative).
		if (rate >= 1) return 0
		return roundCurrency((base / (1 - rate)) - base)
	}

	return roundCurrency(base * rate)
}

export function buildProcessingFeePayload({ amount, processingFee }) {
	const feeDetails = (processingFee && processingFee.feeDetails) || {}

	return {
		processingFeeName: feeDetails.rateName || 'Processing Fee',
		processingFeeAmount: calculateProcessingFee({ amount, processingFee }),
		processingFeeRateType: Number(feeDetails.rateType || 0),
		processingFeeRate: Number(feeDetails.rateAmount || 0),
		processingFeeCalculationMode: (processingFee && processingFee.calculationMode) || 'beforeFee',
	}
}
